import React, { useCallback, useEffect, useState } from 'react';
import { Btn, Input, Select, Spinner, Alert, Modal } from '../../components/UI';
import { api } from '../../utils/api';

// Delivering step of the customer workspace (model UC5 — "Delivering, and what we keep").
// Before the day: the day's materials (running order, Units, masters). The room itself is
// never recorded. After the day: keep the log, the materials used and any adaptation made
// for this client; an adaptation worth sharing goes to the custodians of an area.

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const ta = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)', fontFamily: 'var(--font-body)', fontSize: '0.86rem', resize: 'vertical' };
const label = { display: 'block', fontSize: '0.74rem', fontWeight: 700, color: 'var(--ink-soft)', margin: '10px 0 4px' };

function DayMaterials({ id, onClose }) {
  const [m, setM] = useState(null);
  useEffect(() => { api.deliver.getDayMaterials(id).then(setM).catch(e => setM({ error: e.message })); }, [id]);
  return (
    <Modal open onClose={onClose} title={m && !m.error ? `Day ${m.day} — prepare` : 'Day materials'}>
      {!m ? <Spinner /> : m.error ? <Alert type="error">{m.error}</Alert> : (
        <div style={{ fontSize: '0.86rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={label}>Running order</div>
            {m.runSheet.length ? m.runSheet.map((r, i) => (
              <div key={i} style={{ padding: '3px 0' }}>{r.time ? <b>{r.time} </b> : null}{r.content}{r.units.length ? <span style={{ color: 'var(--ink-faint)' }}> · {r.units.join(', ')}</span> : null}</div>
            )) : <div style={{ color: 'var(--ink-faint)' }}>No slots for this day.</div>}
          </div>
          {m.units.length > 0 && (
            <div>
              <div style={label}>Units — and who has mastered them</div>
              {m.units.map(u => (
                <div key={u.code} style={{ padding: '3px 0' }}><b>{u.code}</b> {u.name}{u.masters ? <span style={{ color: 'var(--ink-soft)' }}> · masters: {u.masters}</span> : null}{(u.flipcharts || u.sheets) ? <span style={{ color: 'var(--ink-faint)' }}> · {u.flipcharts} flipcharts, {u.sheets} sheets</span> : null}</div>
              ))}
            </div>
          )}
          {m.memoryCards.length > 0 && <div><div style={label}>Memory cards</div>{m.memoryCards.join(' · ')}</div>}
          {m.logs.length > 0 && <div><div style={label}>Expedition logs for these Units</div>{m.logs.join(' · ')}</div>}
          <div style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>{m.craftNote}</div>
        </div>
      )}
    </Modal>
  );
}

function CloseDayForm({ day, areas, onDone, onCancel }) {
  const [f, setF] = useState({ report: '', materialsUsed: '', adaptation: '', share: false, area: areas[0] || '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  async function submit() {
    setBusy(true);
    try {
      await api.deliver.closeDay(day.id, { report: f.report, materialsUsed: f.materialsUsed, adaptation: f.adaptation, shareArea: f.share ? f.area : null });
      onDone();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', padding: '10px 12px', marginTop: 8 }}>
      <label style={label}>Log of the day <span style={{ fontWeight: 400 }}>(what happened, what the group took away — not how you ran the room)</span></label>
      <textarea rows={3} value={f.report} onChange={e => set('report', e.target.value)} style={ta} />
      <label style={label}>Materials used <span style={{ fontWeight: 400 }}>(sheets, flipcharts, memory cards)</span></label>
      <Input value={f.materialsUsed} onChange={e => set('materialsUsed', e.target.value)} placeholder="e.g. WS-12, FC-4, memory cards IE001" />
      <label style={label}>Adaptation for this client <span style={{ fontWeight: 400 }}>(optional)</span></label>
      <textarea rows={2} value={f.adaptation} onChange={e => set('adaptation', e.target.value)} style={ta} placeholder="What you changed in a Unit for this customer, and why" />
      {f.adaptation.trim() && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8, fontSize: '0.82rem' }}>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.share} onChange={e => set('share', e.target.checked)} /> Worth sharing — send it to the custodians of
          </label>
          <Select value={f.area} onChange={e => set('area', e.target.value)} disabled={!f.share} style={{ width: 230, padding: '5px 8px' }}>
            {areas.map(a => <option key={a} value={a}>{a}</option>)}
          </Select>
          {f.share && <span style={{ color: 'var(--ink-faint)', width: '100%' }}>Approved, it becomes Hansen Beck’s — credited to you. Leave out anything confidential to the client.</span>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <Btn size="sm" loading={busy} disabled={!f.report.trim()} onClick={submit}>Close the day</Btn>
        <Btn size="sm" variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}

export default function DeliveryDays({ projectId, delivered }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);       // day id being closed
  const [materials, setMaterials] = useState(null);
  const load = useCallback(() => { api.deliver.getProjectDeliveries(projectId).then(setD).catch(e => setD({ error: e.message })); }, [projectId]);
  useEffect(() => { load(); }, [load]);

  async function schedule(day, date) { try { await api.deliver.scheduleDay(day.id, date); load(); } catch (e) { alert(e.message); } }

  if (!d) return <Spinner />;
  if (d.error) return <Alert type="error">{d.error}</Alert>;
  if (!d.days.length) return <p style={{ fontSize: '0.86rem', color: 'var(--ink-faint)' }}>No delivery days yet — they are created from the confirmed programme when the customer is won.</p>;
  const done = d.days.filter(x => x.status === 'delivered').length;

  return (
    <div>
      <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginBottom: 6 }}>{d.programme ? <b>{d.programme}</b> : null} · {done} of {d.days.length} days logged</div>
      {d.days.map(day => (
        <div key={day.id} style={{ borderTop: '1px solid var(--line)', padding: '8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: '0.86rem' }}>
            <b style={{ minWidth: 48 }}>Day {day.day}</b>
            {day.status === 'delivered'
              ? <span style={{ color: 'var(--ink-soft)' }}>✓ logged {fmt(day.deliveredAt)}{day.adaptationShared ? ' · adaptation sent to custodians' : ''}</span>
              : <Input type="date" value={day.plannedDate ? String(day.plannedDate).slice(0, 10) : ''} onChange={e => e.target.value && schedule(day, e.target.value)} style={{ width: 150, padding: '4px 8px' }} />}
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
              <Btn size="sm" variant="ghost" onClick={() => setMaterials(day.id)}>Prepare ↗</Btn>
              {day.status !== 'delivered' && open !== day.id && <Btn size="sm" variant="outline" onClick={() => setOpen(day.id)}>Close the day</Btn>}
            </span>
          </div>
          {day.status === 'delivered' && (day.report || day.materialsUsed || day.adaptation) && (
            <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', margin: '4px 0 0 58px', lineHeight: 1.5 }}>
              {day.report && <div>{day.report}</div>}
              {day.materialsUsed && <div><b>Materials:</b> {day.materialsUsed}</div>}
              {day.adaptation && <div><b>Adaptation:</b> {day.adaptation}</div>}
            </div>
          )}
          {open === day.id && <CloseDayForm day={day} areas={d.areas} onCancel={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />}
        </div>
      ))}
      {done === d.days.length && !delivered && (
        <Alert type="info">All days are logged. When the programme is complete, mark it delivered — the journey goes back to Explore.</Alert>
      )}
      {materials && <DayMaterials id={materials} onClose={() => setMaterials(null)} />}
    </div>
  );
}
